import { DateInput } from "@/shared/components/domain/qpm/inputs/date-input";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  Offer,
  SetOfferValidUntilDateRequest,
} from "@/services/backend/qpm/offers/types/offer";
import { useQpmOfferSetValidUntilDateMutation } from "@/services/backend/qpm/offers/api/offer-api";
import t from "@/lang/lang";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  fieldErrorsForPrefix,
  rtkErrIsValidationError,
  resetField,
} from "@/shared/lib/errors/validation-error";

type OfferDateSelectorProps = {
  offer: Offer;
};

function normalizeToUTCDate(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
}

function validateValidUntilDate(validUntilDate: string | null): string | null {
  if (!validUntilDate) {
    return null;
  }

  const selectedDate = normalizeToUTCDate(new Date(validUntilDate));
  if (Number.isNaN(selectedDate.getTime())) {
    return null;
  }

  if (selectedDate < normalizeToUTCDate(new Date())) {
    return "Das 'Gültig bis'-Datum darf nicht in der Vergangenheit liegen.";
  }

  return null;
}

export function OfferValidUntilDateSelector({ offer }: OfferDateSelectorProps) {
  const { validationError, setValidationError } = useOfferContext();
  const fieldErrors = fieldErrorsForPrefix(validationError, "");
  const [localFieldError, setLocalFieldError] = useState<string | null>(null);

  const [request, setRequest] = useState<SetOfferValidUntilDateRequest>({
    offerId: offer.id,
    validUntilDate: offer.validUntilDate ?? null,
  });
  const [setDate, { error, isLoading, isSuccess, reset }] =
    useQpmOfferSetValidUntilDateMutation();

  const onChangeDate = useCallback(
    (validUntilDate: string | null) => {
      const immediateValidationError = validateValidUntilDate(validUntilDate);

      if (immediateValidationError) {
        setLocalFieldError(immediateValidationError);
        setValidationError({
          message: validationError?.message ?? "the given data was invalid",
          errors: {
            ...(validationError?.errors ?? {}),
            validUntilDate: [immediateValidationError],
          },
        });
      } else {
        setLocalFieldError(null);
        setValidationError(resetField(validationError, "validUntilDate"));
      }

      setRequest((prev) => ({ ...prev, validUntilDate }));
    },
    [setRequest, setValidationError, validationError],
  );

  useEffect(() => {
    if (!rtkErrIsValidationError(error)) {
      return;
    }

    setValidationError(error.data);
  }, [error, setValidationError]);

  const mergedFieldErrors = useMemo(() => {
    if (localFieldError) {
      return [localFieldError];
    }
    return fieldErrors?.validUntilDate;
  }, [fieldErrors?.validUntilDate, localFieldError]);

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setDate,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    { toastError: false, skip: localFieldError !== null },
  );

  return (
    <DateInput
      label={t("Gültig bis")}
      date={request.validUntilDate}
      onDateChange={onChangeDate}
      disabled={offer.immutable}
      fieldErrors={mergedFieldErrors}
      resetFieldError={() => {
        setLocalFieldError(null);
        setValidationError(resetField(validationError, "validUntilDate"));
      }}
    />
  );
}
